import { useCallback } from "react";
import { useNavigate } from "react-router-dom";

/**
 * Back for a card flow: return to wherever the flow was opened from (the hub
 * card, Past Sessions), and to `fallback` when there is nowhere in-app to go.
 *
 * "Nowhere in-app" is read from `window.history.state.idx` — the position
 * react-router records for each entry IT created in this tab: seeded to 0
 * whenever it finds no index on the entry (a cold paint, or a tab arriving from
 * outside the app), +1 per push, and LEFT ALONE by a replace. So `idx === 0`
 * means this entry is the first one we created, and `navigate(-1)` from here
 * would either do nothing or leave the site.
 *
 * It assumes a flow is ENTERED BY A PUSH, which every caller does today
 * (`HomeHubPage`, `PastSessionsPage`). Entering one with `replace: true` would
 * leave `idx` pointing at the opener's predecessor, and Back would skip the
 * opener.
 *
 * This replaces a `location.key === "default"` check, which reads as equivalent
 * and is not. A flow opened from a reflection reminder text arrives with a
 * `?token` that `MePersonLinkRedeemer` scrubs via
 * `navigate(pathname, { replace: true })` — and react-router mints a fresh
 * RANDOM key on a replace. The key therefore stopped saying "first entry" for
 * every link we actually send, while `idx` still does.
 *
 * Read at click time rather than at render, so the scrub is always accounted
 * for. An unreadable index (no browser history at all — a `MemoryRouter` under
 * test, or no `window`) takes the fallback, which is the safe direction: better
 * to send someone Home than out of the app.
 */
export function useFlowBack(fallback = "/me/home"): () => void {
  const navigate = useNavigate();
  return useCallback(() => {
    const historyIndex =
      typeof window === "undefined"
        ? undefined
        : (window.history.state as { idx?: unknown } | null)?.idx;
    if (typeof historyIndex === "number" && historyIndex > 0) {
      navigate(-1);
    } else {
      navigate(fallback);
    }
  }, [fallback, navigate]);
}
